import React, { useState, useRef, useCallback } from "react";
import Cropper from "react-easy-crop";
import { storage, auth, db } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { Modal, Button } from "react-bootstrap";

const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB input limit
const OUTPUT_SIZE = 400; // 400x400px — ideal for profile images
const OUTPUT_QUALITY = 0.88; // WebP quality sweet spot
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

async function getCroppedImg(imageSrc, croppedAreaPixels) {
	const image = await new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = imageSrc;
	});

	const canvas = document.createElement("canvas");
	canvas.width = OUTPUT_SIZE;
	canvas.height = OUTPUT_SIZE;
	const ctx = canvas.getContext("2d");

	ctx.drawImage(
		image,
		croppedAreaPixels.x,
		croppedAreaPixels.y,
		croppedAreaPixels.width,
		croppedAreaPixels.height,
		0,
		0,
		OUTPUT_SIZE,
		OUTPUT_SIZE,
	);

	return new Promise((resolve) => {
		canvas.toBlob((blob) => resolve(blob), "image/webp", OUTPUT_QUALITY);
	});
}

export default function UploadPhoto({ onPhotoCropped }) {
	const [rawPreview, setRawPreview] = useState(null);
	const [croppedPreview, setCroppedPreview] = useState(null);
	const [croppedBlob, setCroppedBlob] = useState(null);
	const [showCropModal, setShowCropModal] = useState(false);

	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

	const [progress, setProgress] = useState(0);
	const [error, setError] = useState(null);
	const [isUploading, setIsUploading] = useState(false);

	const fileInputRef = useRef(null);

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		if (!ALLOWED_TYPES.includes(file.type)) {
			setError("Only JPG, PNG, and WebP images are allowed.");
			e.target.value = null;
			return;
		}

		if (file.size > MAX_SIZE_BYTES) {
			setError("Image must be under 5MB.");
			e.target.value = null;
			return;
		}

		setRawPreview(URL.createObjectURL(file));
		setError(null);
		setShowCropModal(true);
	};

	const onCropComplete = useCallback((_, areaPixels) => {
		setCroppedAreaPixels(areaPixels);
	}, []);

	const handleCropConfirm = async () => {
		try {
			const blob = await getCroppedImg(rawPreview, croppedAreaPixels);
			setCroppedBlob(blob);
			setCroppedPreview(URL.createObjectURL(blob));
			setShowCropModal(false);
			onPhotoCropped?.();
		} catch (err) {
			setError("Error cropping image.");
		}
	};

	const handleCropCancel = () => {
		setShowCropModal(false);
		// If they cancel before ever cropping, clear raw preview
		if (!croppedPreview) setRawPreview(null);
	};

	const handleUpload = async () => {
		if (!croppedBlob) return;
		setIsUploading(true);

		try {
			const filename = `profile_${auth.currentUser.uid}.webp`;
			const storageRef = ref(storage, `profile_pictures/${auth.currentUser.uid}/${filename}`);
			const uploadTask = uploadBytesResumable(storageRef, croppedBlob, {
				contentType: "image/webp",
			});

			uploadTask.on(
				"state_changed",
				(snapshot) => setProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
				(err) => {
					setError(err.message);
					setIsUploading(false);
				},
				async () => {
					try {
						const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

						await updateProfile(auth.currentUser, { photoURL: downloadURL });

						await setDoc(
							doc(db, "users", auth.currentUser.uid),
							{
								photoURL: downloadURL,
								photoMeta: {
									format: "webp",
									width: OUTPUT_SIZE,
									height: OUTPUT_SIZE,
									updatedAt: new Date().toISOString(),
								},
							},
							{ merge: true },
						);

						// ← these must be here, after upload completes
						setIsUploading(false);
						setCroppedPreview(null);
						window.location.reload();
					} catch (err) {
						setError(err.message);
						setIsUploading(false);
					}
				},
			);
		} catch (err) {
			setError("Upload failed: " + err.message);
			setIsUploading(false);
		}
	};

	return (
		<div className="d-flex flex-column align-items-center gap-1">
			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp"
				onChange={handleFileChange}
				style={{ display: "none" }}
			/>

			{croppedPreview && (
				<div
					className="rounded-circle overflow-hidden border border-secondary"
					style={{ width: "120px", height: "120px", cursor: "pointer" }}
					onClick={() => fileInputRef.current.click()}
					title="Click to change photo"
				>
					<img src={croppedPreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
				</div>
			)}

			{error && <p className="text-danger mb-0 small">{error}</p>}

			{progress > 0 && progress < 100 && (
				<div className="progress w-100" style={{ height: "6px" }}>
					<div className="progress-bar" role="progressbar" style={{ width: `${progress}%` }} />
				</div>
			)}

			<div className="d-flex flex-column gap-1">
				{!croppedPreview && (
					<div className="alert alert-warning py-2 px-3 text-center small mb-1" role="alert">
						<i className="bi bi-exclamation-circle me-1"></i>
						You need a profile photo & name to see our events!
					</div>
				)}
				<button className="btn my-btn btn-sm" onClick={() => fileInputRef.current.click()} disabled={isUploading}>
					<i className="bi bi-image me-1"></i>
					{croppedPreview ? "Change photo" : "Choose photo"}
				</button>

				{croppedPreview && (
					<button className="btn my-btn btn-sm" onClick={handleUpload} disabled={isUploading}>
						{isUploading ? (
							<>
								<span className="spinner-border spinner-border-sm me-1" />
								Uploading...
							</>
						) : (
							<>
								<i className="bi bi-cloud-upload me-1"></i>Upload
							</>
						)}
					</button>
				)}
			</div>

			<small className="text-muted mt-1">jpg, png or webp · max 5mb</small>

			<Modal show={showCropModal} onHide={handleCropCancel} centered fullscreen="sm-down">
				<Modal.Header closeButton>
					<Modal.Title>Crop your photo</Modal.Title>
				</Modal.Header>
				<Modal.Body className="p-0">
					<div style={{ position: "relative", width: "100%", height: "350px", background: "#222" }}>
						<Cropper
							image={rawPreview}
							crop={crop}
							zoom={zoom}
							aspect={1}
							cropShape="round"
							showGrid={false}
							onCropChange={setCrop}
							onZoomChange={setZoom}
							onCropComplete={onCropComplete}
						/>
					</div>
					<div className="px-4 pt-3 pb-2">
						<label className="form-label small text-muted mb-1">Zoom</label>
						<input
							type="range"
							className="form-range"
							min={1}
							max={3}
							step={0.01}
							value={zoom}
							onChange={(e) => setZoom(Number(e.target.value))}
						/>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={handleCropCancel}>
						Cancel
					</Button>
					<Button className="my-btn" onClick={handleCropConfirm}>
						<i className="bi bi-check-lg me-1"></i>Apply crop
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
